import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { formatDate, formatNumber } from "@/lib/format";
import { kb } from "@/components/settings/backup/util";

const PHRASE = "GERİ YÜKLE";
const Step = ({ n, step, label }) => (
  <div className={`flex-1 text-center text-[11px] py-1 rounded ${step >= n ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500"}`}>{n}. {label}</div>
);

export function RestoreDialog({ backup, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [text, setText] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { setStep(1); setText(""); setPw(""); setResult(null); }, [backup]);
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post("/backup/restore", { backup_id: backup.id, confirm_text: text, password: pw }); setResult({ ok: true, data }); onDone(); }
    catch (e) { setResult({ ok: false, msg: apiError(e) }); }
    setBusy(false);
    setStep(4);
  };
  if (!backup) return null;
  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="restore-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700"><AlertTriangle className="h-4 w-4" />Yedekten Geri Yükle</DialogTitle>
          <DialogDescription>Yalnızca Süper Yönetici. Tüm adımlar denetim kaydına yazılır.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1 my-2"><Step n={1} step={step} label="Uyarı" /><Step n={2} step={step} label="Onay" /><Step n={3} step={step} label="Parola" /><Step n={4} step={step} label="Sonuç" /></div>
        {step === 1 && <div className="text-[13px] space-y-2" data-testid="restore-step-warning">
          <div className="rounded border border-rose-200 bg-rose-50 p-3 text-rose-800">Canlı veritabanındaki tüm iş verileri <b>{formatDate(backup.started_at)}</b> tarihli yedekle değiştirilecek. Bu tarihten sonra girilen kayıtlar kaybolur.</div>
          <div className="text-slate-600">{backup.name} · {kb(backup.size_bytes)} · {formatNumber(backup.documents)} belge · {backup.collection_count} koleksiyon · v{backup.app_version}</div>
          <ul className="list-disc pl-5 text-slate-600 text-[12px]"><li>Önce mevcut durumun doğrulanmış güvenlik yedeği alınır.</li><li>Yedek ayrı bir alana yüklenip doğrulanır, sonra canlıya taşınır.</li><li>Hata olursa güvenlik yedeğinden otomatik geri dönülür. Denetim kaydı korunur.</li></ul>
        </div>}
        {step === 2 && <div className="text-[13px] space-y-2" data-testid="restore-step-confirm">
          <p>Devam etmek için <b className="font-mono">{PHRASE}</b> yazın:</p>
          <Input value={text} onChange={(e) => setText(e.target.value)} autoFocus data-testid="restore-confirm-input" />
        </div>}
        {step === 3 && <div className="text-[13px] space-y-2" data-testid="restore-step-password">
          <p>Kimliğinizi doğrulamak için parolanızı yeniden girin:</p>
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus data-testid="restore-password-input" />
        </div>}
        {step === 4 && result && <div className={`text-[13px] rounded p-3 ${result.ok ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`} data-testid="restore-result">
          {result.ok ? <><CheckCircle2 className="h-4 w-4 inline mr-1" />Geri yükleme BAŞARILI: {result.data.collections} koleksiyon, {formatNumber(result.data.documents)} belge doğrulandı. Güvenlik yedeği: {result.data.safety_backup}</> : `Geri yükleme başarısız: ${result.msg}`}
        </div>}
        <DialogFooter>
          {step < 4 && <Button variant="outline" disabled={busy} onClick={onClose} data-testid="restore-cancel">Vazgeç</Button>}
          {step === 1 && <Button variant="destructive" onClick={() => setStep(2)} data-testid="restore-next-1">Anladım, devam et</Button>}
          {step === 2 && <Button variant="destructive" disabled={text.trim() !== PHRASE} onClick={() => setStep(3)} data-testid="restore-next-2">Devam</Button>}
          {step === 3 && <Button variant="destructive" disabled={!pw || busy} onClick={submit} data-testid="restore-submit">{busy ? "Geri yükleniyor…" : "Geri Yükle"}</Button>}
          {step === 4 && <Button onClick={() => (result?.ok ? window.location.reload() : onClose())} data-testid="restore-close">{result?.ok ? "Sayfayı yenile" : "Kapat"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
